import React from 'react';
import {
  Box,
  Chip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Typography,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

const EventFilters = ({ 
  filters, 
  onFiltersChange, 
  eventCounts = {}, 
  availableYears = [],
  loading = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const statusOptions = [
    { value: 'all', label: 'All Events', color: 'default' },
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'upcoming', label: 'Upcoming', color: 'warning' },
    { value: 'closed', label: 'Closed', color: 'info' },
    { value: 'archived', label: 'Archived', color: 'default' }
  ];

  const handleStatusChange = (status) => {
    onFiltersChange({
      ...filters,
      status,
      // Reset year filter when changing status (except for archived/all)
      year: (status === 'archived' || status === 'all') ? filters.year : 'all'
    });
  };

  const handleSearchChange = (event) => {
    onFiltersChange({
      ...filters,
      search: event.target.value
    });
  };

  const handleYearChange = (event) => {
    onFiltersChange({
      ...filters,
      year: event.target.value
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      status: 'all',
      year: 'all',
      search: '',
      limit: 20
    });
  };

  const getStatusLabel = (option) => {
    const count = eventCounts[option.value] || 0;
    return `${option.label} (${count})`;
  };

  const showYearFilter = filters.status === 'archived' || filters.status === 'all';
  const hasActiveFilters = filters.status !== 'all' || filters.year !== 'all' || filters.search;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Status Filter Pills */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Filter by Status
        </Typography>
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            flexWrap: 'wrap', 
            gap: 1,
            '& > *': { mb: 1 } // Add margin bottom for wrapped items
          }}
        >
          {statusOptions.map((option) => (
            <Chip
              key={option.value}
              label={getStatusLabel(option)}
              color={filters.status === option.value ? option.color : 'default'}
              variant={filters.status === option.value ? 'filled' : 'outlined'}
              onClick={() => handleStatusChange(option.value)}
              disabled={loading}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filters.status === option.value 
                    ? undefined 
                    : 'action.hover'
                }
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Search and Year Filter Row */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'flex-end',
        flexDirection: isMobile ? 'column' : 'row',
        '& > *': { 
          minWidth: isMobile ? '100%' : 'auto'
        }
      }}>
        {/* Search Field */}
        <TextField
          placeholder="Search events, themes, descriptions..."
          value={filters.search}
          onChange={handleSearchChange}
          disabled={loading}
          size="small"
          sx={{ 
            flexGrow: 1,
            minWidth: 250
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <ClearIcon 
                  fontSize="small" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSearchChange({ target: { value: '' } })}
                />
              </InputAdornment>
            )
          }}
        />

        {/* Year Filter (conditional) */}
        {showYearFilter && availableYears.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={filters.year}
              label="Year"
              onChange={handleYearChange}
              disabled={loading}
            >
              <MenuItem value="all">All Years</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Chip
            label="Clear Filters"
            variant="outlined"
            onClick={clearFilters}
            onDelete={clearFilters}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Box>

      {/* Results Summary */}
      {hasActiveFilters && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {loading ? 'Filtering events...' : `Filtered results`}
        </Typography>
      )}
    </Box>
  );
};

export default EventFilters;
